import '../assets/Text.css';
import { ref, computed, defineComponent, openBlock, createElementBlock, normalizeClass, unref, normalizeStyle, withModifiers, createElementVNode, createCommentVNode, Fragment, createVNode, renderList, createBlock, toDisplayString, resolveDynamicComponent } from 'vue';
import { u as useI18n } from './widgets-core.es.C7uAbUpp.js';
import { d as debounce } from './debounce.CJgNZ1TL.js';
import { h as ButtonActionKind, A as AgentActionKind, e as ToolModuleKind, i as ButtonKind, g as DefaultStep, L as LoginStep, D as DeviceKind, j as ToolSize, T as TextTransform } from './maps.nxkzRTum.js';
import { q as useLiterals, e as appendPx, i as appendEm, o as useStore, s as sanitizeMessage, B as ButtonState } from './useStore.B0k3KXLh.js';

/**
 * Gets the first element of `array`.
 *
 * @static
 * @memberOf _
 * @since 0.1.0
 * @alias first
 * @category Array
 * @param {Array} array The array to query.
 * @returns {*} Returns the first element of `array`.
 * @example
 *
 * _.head([1, 2, 3]);
 * // => 1
 *
 * _.head([]);
 * // => undefined
 */
function head(array) {
  return (array && array.length) ? array[0] : undefined;
}

const buttonActionTypesMap = {
  [ButtonActionKind.ApplyPromoCode]: AgentActionKind.ApplyDiscount,
  [ButtonActionKind.CloseTool]: AgentActionKind.NoAction,
  [ButtonActionKind.EmailCapture]: AgentActionKind.SendSubscribe,
  emailCapture: AgentActionKind.SendSubscribe,
  [ButtonActionKind.SendForm]: AgentActionKind.SendForm,
  [ButtonActionKind.OpenUrl]: AgentActionKind.OpenUri,
  [ButtonActionKind.CallPhoneNumber]: AgentActionKind.OpenUri,
  [ButtonActionKind.OpenMailClient]: AgentActionKind.OpenUri,
  [ButtonActionKind.SavedSearch]: AgentActionKind.SendSaveSearch,
  savedSearch: AgentActionKind.SendSaveSearch,
  [ButtonActionKind.OpenBookingEngine]: AgentActionKind.OpenBookingEngine,
  [ButtonActionKind.LastSearch]: AgentActionKind.OpenBookingEngine
};
function getButtonActionType(type) {
  return buttonActionTypesMap[type];
}

const i18n = useLiterals();
let ids = 0;
function uniqueId() {
  ids += 1;
  return `extra-modules-${ids}`;
}
function getCommonProps(key) {
  const literal = i18n.getLiteral(key);
  const translation = {
    [i18n.language]: literal
  };
  return {
    sync: true,
    id: uniqueId(),
    mobile: translation,
    desktop: translation
  };
}
function getHeading(key = "") {
  const commonProps = getCommonProps(key);
  return {
    ...commonProps,
    moduleType: ToolModuleKind.Heading
  };
}
function getParagraph(key = "") {
  const commonProps = getCommonProps(key);
  return {
    ...commonProps,
    moduleType: ToolModuleKind.Paragraph
  };
}
function getButton(actionKind, key) {
  const commonProps = getCommonProps(key);
  return {
    ...commonProps,
    moduleType: ToolModuleKind.Button,
    behaviour: {
      type: actionKind
    }
  };
}

const moduleKindToSuccessMessageMap = {
  [ToolModuleKind.EmailCapture]: "Your email address has been successfully added to our list.",
  [ToolModuleKind.SavedSearch]: "We will send you an email with your search very soon.",
  [ToolModuleKind.Form]: "We have received your form submission correctly"
};
const buttonKindToSuccessMap = {
  [ButtonActionKind.ApplyPromoCode]: "Apply promocode",
  [ButtonActionKind.OpenUrl]: "Continue",
  [ButtonActionKind.CloseTool]: "Close",
  [ButtonActionKind.OpenBookingEngine]: "Book Now",
  [ButtonActionKind.OpenMailClient]: "Contact Now",
  [ButtonActionKind.CallPhoneNumber]: "Call Now"
};

const stepsWrapper = ref();
const ToolModuleKindsWithExtraSteps = [
  ToolModuleKind.SavedSearch,
  ToolModuleKind.EmailCapture,
  ToolModuleKind.Form,
  ToolModuleKind.Login
];
function hasModuleWithSteps(modules) {
  return modules.find(
    (module) => ToolModuleKindsWithExtraSteps.includes(module.moduleType)
  )?.moduleType || "";
}
function getButtonModuleActionKind(modules) {
  return modules.find(
    (module) => module.moduleType === ToolModuleKind.Button
  )?.behaviour.type;
}
function getSecondaryButtons(modules, primaryButtonId) {
  return modules.filter((module) => {
    if (module.moduleType !== ToolModuleKind.Button) return false;
    const buttonModule = module;
    return buttonModule.kind === ButtonKind.Secondary && buttonModule.primaryButtonId === primaryButtonId;
  });
}
function groupSecondaryButtonModules(modules) {
  return modules.reduce((acc, module) => {
    if (module.moduleType !== ToolModuleKind.Button) return [...acc, module];
    const buttonModule = module;
    if (buttonModule.kind === ButtonKind.Secondary) return acc;
    const secondaryButtons = getSecondaryButtons(modules, module.id);
    const res = [...acc, {
      ...module,
      alignment: head(secondaryButtons)?.alignment,
      secondaryButtons
    }];
    return res;
  }, []);
}
function useSteps(modules) {
  const processedModules = groupSecondaryButtonModules(modules);
  const steps = {
    [DefaultStep.Main]: processedModules
  };
  const moduleWithSteps = hasModuleWithSteps(processedModules);
  const hasLoginModule = processedModules.find(
    (module) => module.moduleType === ToolModuleKind.Login
  );
  if (!moduleWithSteps) return steps;
  if (hasLoginModule) {
    const buttonModule = processedModules.find(
      (module) => module.moduleType === ToolModuleKind.Button
    );
    steps[DefaultStep.Main] = processedModules.reduce((acc, module) => {
      if (module.moduleType === ToolModuleKind.Button) {
        return acc;
      }
      if (module.moduleType === ToolModuleKind.Login) {
        return [
          ...acc,
          {
            ...module,
            buttonModule,
            currentView: LoginStep.LoginRegister
          }
        ];
      }
      return [...acc, module];
    }, []);
    steps[LoginStep.LoginSignin] = processedModules.reduce((acc, module) => {
      if (module.moduleType !== ToolModuleKind.Login) {
        return acc;
      }
      if (module.moduleType === ToolModuleKind.Login) {
        return [
          ...acc,
          {
            ...module,
            buttonModule,
            currentView: LoginStep.LoginSignin
          }
        ];
      }
      return [...acc, module];
    }, []);
    if (!moduleWithSteps) {
      return steps;
    }
    const buttonModuleActionKind2 = getButtonModuleActionKind(processedModules);
    const buttonModuleText2 = buttonKindToSuccessMap[buttonModuleActionKind2] || "Close";
    steps[LoginStep.LoginSuccess] = processedModules.reduce((acc, module) => {
      if (module.moduleType !== ToolModuleKind.Login && module.moduleType !== ToolModuleKind.Button) {
        return acc;
      }
      if (module.moduleType === ToolModuleKind.Button) {
        return [
          ...acc,
          {
            ...getButton(buttonModuleActionKind2, buttonModuleText2)
          }
        ];
      }
      if (module.moduleType === ToolModuleKind.Login) {
        return [
          ...acc,
          {
            ...module,
            currentView: LoginStep.LoginSuccess
          }
        ];
      }
      return [...acc, module];
    }, []);
    const failureHeading2 = computed(() => getHeading("There has been an error."));
    const failureParagraph2 = computed(() => getParagraph("Something went wrong. Please, try again later."));
    const failureButton2 = computed(() => getButton(ButtonActionKind.GoMainStep, "Back"));
    steps[DefaultStep.Failure] = [
      failureHeading2.value,
      failureParagraph2.value,
      failureButton2.value
    ];
    return steps;
  }
  const buttonModuleActionKind = getButtonModuleActionKind(processedModules);
  const buttonModuleText = buttonKindToSuccessMap[buttonModuleActionKind] || "Close";
  const successHeading = computed(() => getHeading("Thank you!"));
  const successParagraph = computed(() => getParagraph(
    moduleKindToSuccessMessageMap[moduleWithSteps]
  ));
  const successButton = computed(
    () => getButton(buttonModuleActionKind, buttonModuleText)
  );
  const failureHeading = computed(() => getHeading("There has been an error."));
  const failureParagraph = computed(() => getParagraph("Something went wrong. Please, try again later."));
  const failureButton = computed(() => getButton(ButtonActionKind.GoMainStep, "Back"));
  steps[DefaultStep.Success] = [
    successHeading.value,
    successParagraph.value,
    successButton.value
  ];
  steps[DefaultStep.Failure] = [
    failureHeading.value,
    failureParagraph.value,
    failureButton.value
  ];
  if (getButtonModuleActionKind(processedModules) === "submit") {
    steps[DefaultStep.Success].pop();
    steps[DefaultStep.Failure].pop();
  }
  return steps;
}

const defaultAppearance = {
  fontSize: 16,
  lineHeight: 16,
  letterSpacing: 0
};
function useTextAppearance(appearance = defaultAppearance, device = DeviceKind.Desktop, layoutSize) {
  const {
    fontSize,
    lineHeight,
    letterSpacing
  } = appearance;
  let multiplier;
  if (device === DeviceKind.Desktop && layoutSize === ToolSize.Compact) {
    multiplier = 0.865;
  } else if (device === DeviceKind.Mobile && fontSize > 12) {
    multiplier = 0.8;
  } else {
    multiplier = 1;
  }
  let adjustedFontSize = Math.round(fontSize * multiplier);
  if (device === DeviceKind.Mobile && fontSize > 12 && adjustedFontSize < 12) {
    adjustedFontSize = 12;
  }
  const adjustedLineHeight = Math.round(lineHeight);
  const adjustedLetterspacing = (letterSpacing ?? 0) / 100;
  return {
    fontSize: appendPx(adjustedFontSize),
    lineHeight: appendPx(adjustedLineHeight),
    letterSpacing: !letterSpacing ? "normal" : appendEm(adjustedLetterspacing)
  };
}

const _hoisted_1$1 = ["button-state", "moduleType", "data-module-id", "data-full-width", "aria-label", "aria-busy"];
const _hoisted_2$1 = ["innerHTML"];
const _hoisted_3 = ["innerHTML"];
const _hoisted_4 = ["aria-label"];
const _sfc_main$2 = /* @__PURE__ */ defineComponent({
  __name: "Button",
  props: {
    moduleProps: {},
    hasExtraSteps: {},
    isLinkLayout: { type: Boolean },
    isSecondary: { type: Boolean },
    toolKind: {},
    layout: {}
  },
  emits: ["show-promo-code-fallback"],
  setup(__props, { emit: __emit }) {
    const emit = __emit;
    const store = useStore();
    const { t } = useI18n();
    const props = __props;
    const text = computed(() => store?.literals.getActiveModuleText(
      props.moduleProps,
      store?.styles.fonts[props.moduleProps.moduleType]?.textTransform === TextTransform.Titlecase,
      {
        candidates: store.extraData.dynamicTags,
        replacement: store.extraData.tokens
      }
    ));
    const rootStyle = computed(
      () => useTextAppearance(
        props.moduleProps.appearance,
        store?.device,
        props.layout?.size
      )
    );
    const onClick = debounce(async () => {
      if (store?.handlers.buttonState !== ButtonState.Sending) {
        if (props.hasExtraSteps && !props.moduleProps.id.includes("extra")) {
          if (stepsWrapper.value) {
            stepsWrapper.value.scrollTop = 0;
          }
          store?.handlers.callHandler(getButtonActionType(props.hasExtraSteps));
        } else if (props.moduleProps.behaviour.type === ButtonActionKind.GoMainStep) {
          store?.handlers.goBackToMainStep();
        } else {
          const buttonActionType = getButtonActionType(props.moduleProps.behaviour.type);
          const result = await store?.handlers.callHandler(buttonActionType, props.moduleProps.id);
          if (!result && props.moduleProps.behaviour.type === ButtonActionKind.ApplyPromoCode) {
            emit("show-promo-code-fallback");
          }
        }
      }
    }, 200, { leading: true, trailing: false });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("button", {
        class: normalizeClass([
          "thn--tool-button",
          {
            "inline-layout": _ctx.isLinkLayout,
            "thn--tool-button__secondary": _ctx.isSecondary,
            "bg-color": !_ctx.isLinkLayout,
            "bg-color-secondary": !_ctx.isLinkLayout && _ctx.isSecondary
          }
        ]),
        "button-state": unref(store)?.handlers.buttonState,
        style: normalizeStyle(rootStyle.value),
        moduleType: _ctx.moduleProps.moduleType,
        "data-module-id": _ctx.moduleProps.id,
        "data-full-width": _ctx.moduleProps.appearance?.fullWidth,
        onMousedown: _cache[0] || (_cache[0] = withModifiers(
          //@ts-ignore
          (...args) => unref(onClick) && unref(onClick)(...args),
          ["left"]
        )),
        onClick: _cache[1] || (_cache[1] = //@ts-ignore
        (...args) => unref(onClick) && unref(onClick)(...args)),
        "aria-label": text.value && unref(sanitizeMessage)(text.value),
        "aria-busy": unref(store)?.handlers.buttonState === unref(ButtonState).Sending ? "true" : "false"
      }, [
        !_ctx.isLinkLayout ? (openBlock(), createElementBlock("span", {
          key: 0,
          class: "thn--tool-button__label",
          innerHTML: text.value
        }, null, 8, _hoisted_2$1)) : (openBlock(), createElementBlock("span", {
          key: 1,
          innerHTML: text.value
        }, null, 8, _hoisted_3)),
        unref(store)?.handlers.buttonState === unref(ButtonState).Sending && !_ctx.isLinkLayout ? (openBlock(), createElementBlock("div", {
          key: 2,
          class: "thn--tool-button__sending loader",
          "aria-label": unref(t)("Sending"),
          "aria-live": "polite"
        }, _cache[2] || (_cache[2] = [
          createElementVNode("div", { class: "la-ball-pulse" }, [
            createElementVNode("div", { "aria-hidden": "true" }),
            createElementVNode("div", { "aria-hidden": "true" }),
            createElementVNode("div", { "aria-hidden": "true" })
          ], -1)
        ]), 8, _hoisted_4)) : createCommentVNode("", true)
      ], 46, _hoisted_1$1);
    };
  }
});

const _hoisted_1 = ["data-button-alignment"];
const _hoisted_2 = {
  key: 0,
  class: "thn--tool-cta-group-promo-code-fallback text-sm"
};
const _sfc_main$1 = /* @__PURE__ */ defineComponent({
  __name: "CTAGroup",
  props: {
    moduleProps: {},
    hasExtraSteps: {},
    toolKind: {},
    layout: {}
  },
  setup(__props) {
    const props = __props;
    const { t } = useI18n();
    const showPromoCodeFallback = ref(false);
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock(Fragment, null, [
        createElementVNode("div", {
          class: "thn--tool-cta-group",
          "data-button-alignment": props.moduleProps.alignment ?? "horizontal"
        }, [
          createVNode(_sfc_main$2, {
            "has-extra-steps": props.hasExtraSteps,
            "tool-kind": props.toolKind,
            layout: props.layout,
            "is-link-layout": props.moduleProps.appearance?.layout === "link",
            "module-props": props.moduleProps,
            onShowPromoCodeFallback: _cache[0] || (_cache[0] = ($event) => {
              showPromoCodeFallback.value = true;
            })
          }, null, 8, ["has-extra-steps", "tool-kind", "layout", "is-link-layout", "module-props"]),
          (openBlock(true), createElementBlock(Fragment, null, renderList(_ctx.moduleProps.secondaryButtons, (buttonProps) => {
            return openBlock(), createBlock(_sfc_main$2, {
              key: buttonProps.id,
              "has-extra-steps": props.hasExtraSteps,
              "tool-kind": props.toolKind,
              "is-link-layout": buttonProps.appearance?.layout === "link",
              "is-secondary": true,
              layout: props.layout,
              "module-props": buttonProps,
              onShowPromoCodeFallback: _cache[1] || (_cache[1] = ($event) => showPromoCodeFallback.value = true)
            }, null, 8, ["has-extra-steps", "tool-kind", "is-link-layout", "layout", "module-props"]);
          }), 128))
        ], 8, _hoisted_1),
        showPromoCodeFallback.value ? (openBlock(), createElementBlock("div", _hoisted_2, [
          createElementVNode("p", null, " ℹ️ " + toDisplayString(unref(t)("Use the promocode {behaviour} when making your reservation to enjoy your discount.", { behaviour: props.moduleProps.behaviour.value })), 1)
        ])) : createCommentVNode("", true)
      ], 64);
    };
  }
});

const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "Text",
  props: {
    moduleProps: {},
    toolKind: {},
    layout: {}
  },
  setup(__props) {
    const moduleKindToDefault = {
      [ToolModuleKind.Heading]: {
        fontSize: 24,
        lineHeight: 24
      },
      [ToolModuleKind.Paragraph]: {
        fontSize: 16,
        lineHeight: 16
      },
      [ToolModuleKind.Button]: {
        fontSize: 18,
        lineHeight: 18
      }
    };
    const store = useStore();
    const props = __props;
    const text = computed(() => store?.literals.getActiveModuleText(
      props.moduleProps,
      store?.styles.fonts[props.moduleProps.moduleType]?.textTransform === TextTransform.Titlecase,
      {
        candidates: store.extraData.dynamicTags,
        replacement: store.extraData.tokens
      }
    ));
    const textAppearance = computed(
      () => props.moduleProps.appearance || moduleKindToDefault[props.moduleProps.moduleType]
    );
    const textStyle = computed(
      () => useTextAppearance(textAppearance.value, store?.device, props.layout?.size)
    );
    const tag = computed(() => {
      if (props.moduleProps.moduleType === "heading") {
        return "h2";
      }
      if (props.moduleProps.moduleType === "paragraph") {
        return "p";
      }
      return "div";
    });
    return (_ctx, _cache) => {
      return openBlock(), createBlock(resolveDynamicComponent(tag.value), {
        class: "thn--tool-text",
        style: normalizeStyle(textStyle.value),
        innerHTML: text.value,
        "module-type": _ctx.moduleProps.moduleType,
        "data-module-id": _ctx.moduleProps.id,
        tabindex: "-1"
      }, null, 8, ["style", "innerHTML", "module-type", "data-module-id"]);
    };
  }
});

export { _sfc_main as _, useTextAppearance as a, _sfc_main$1 as b, groupSecondaryButtonModules as c, getButtonActionType as g, hasModuleWithSteps as h, stepsWrapper as s, useSteps as u };
//# sourceMappingURL=Text.vue_vue_type_style_index_0_lang.CFVGfjC7.js.map
